import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AdminLayout from '../../components/AdminLayout';
import { adminApi, DonationConfig } from '../../api/admin';

const dollars = (cents: number) => (cents / 100).toFixed(2);
const centsFrom = (value: string) => Math.round(parseFloat(value) * 100);

const Badge: React.FC<{ ok: boolean; label: string; testid: string }> = ({ ok, label, testid }) => (
  <span className={`sgk-chip ${ok ? 'sgk-chip--live' : 'sgk-chip--off'}`} data-testid={testid}>
    {label}
  </span>
);

const DonationsSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: config, isLoading } = useQuery<DonationConfig>({
    queryKey: ['donation-config'],
    queryFn: adminApi.getDonationConfig,
  });

  // Local form state, hydrated from the config once it loads.
  const [donationsEnabled, setDonationsEnabled] = useState(false);
  const [minAmount, setMinAmount] = useState('');
  const [descriptor, setDescriptor] = useState('');
  const [formError, setFormError] = useState('');
  const [saved, setSaved] = useState(false);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    if (!config) return;
    setDonationsEnabled(config.donations_enabled);
    setMinAmount(dollars(config.min_amount_cents));
    setDescriptor(config.statement_descriptor);
  }, [config]);

  const saveMutation = useMutation({
    mutationFn: adminApi.updateDonationConfig,
    onSuccess: (data) => {
      queryClient.setQueryData(['donation-config'], data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (err: any) => {
      const d = err.response?.data;
      setFormError(
        d?.min_amount_cents?.[0] ||
          d?.detail ||
          'Could not save. Check the amounts and try again.',
      );
    },
  });

  const onSave = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    const minCents = centsFrom(minAmount);
    if (Number.isNaN(minCents)) {
      setFormError('The minimum gift must be a number, in dollars.');
      return;
    }
    saveMutation.mutate({
      donations_enabled: donationsEnabled,
      min_amount_cents: minCents,
      statement_descriptor: descriptor,
    });
  };

  const startOnboarding = async () => {
    setConnecting(true);
    try {
      const { url } = await adminApi.connectOnboard();
      window.location.href = url; // hand off to Stripe-hosted onboarding
    } catch {
      setConnecting(false);
    }
  };

  const openDashboard = async () => {
    const { url } = await adminApi.connectLoginLink();
    window.open(url, '_blank', 'noopener');
  };

  if (isLoading || !config) {
    return (
      <AdminLayout title="Donations">
        <p className="sgk-admin__sub" data-testid="donations-loading">Loading…</p>
      </AdminLayout>
    );
  }

  const connected = Boolean(config.stripe_account_id);
  const connectLabel = !connected
    ? 'Connect Stripe'
    : config.details_submitted
      ? 'Update Stripe details'
      : 'Continue onboarding';

  return (
    <AdminLayout title="Donations">

      {/* Stripe connection + status */}
      <section className="sgk-panel">
        <h2 className="sgk-panel__title">Stripe Connect</h2>
        <p className="sgk-panel__hint">
          Donations settle straight into your own Stripe account.
        </p>
        <div className="sgk-chiprow">
          <Badge ok={connected} label={connected ? 'Account created' : 'Not connected'} testid="donations-status-connected" />
          <Badge ok={config.charges_enabled} label="Charges enabled" testid="donations-status-charges" />
          <Badge ok={config.payouts_enabled} label="Payouts enabled" testid="donations-status-payouts" />
          <Badge ok={config.details_submitted} label="Details submitted" testid="donations-status-details" />
          <Badge ok={config.is_ready} label="Ready to accept gifts" testid="donations-status-ready" />
        </div>
        <div className="sgk-btnrow">
          <button
            type="button"
            onClick={startOnboarding}
            disabled={connecting}
            className="sgk-btn sgk-btn--navy sgk-btn--sm"
            data-testid="donations-connect-stripe-button"
          >
            {connecting ? 'Redirecting…' : connectLabel}
          </button>
          {connected && (
            <button
              type="button"
              onClick={openDashboard}
              className="sgk-btn sgk-btn--ghost sgk-btn--sm"
              data-testid="donations-open-dashboard-button"
            >
              Open Stripe dashboard
            </button>
          )}
        </div>
      </section>

      {/* Donation configuration */}
      <section className="sgk-panel">
        <h2 className="sgk-panel__title">Configuration</h2>
        <p className="sgk-panel__hint">How gifts are presented and settled.</p>
        {formError && (
          <div className="sgk-alert sgk-alert--error" data-testid="donations-form-error">
            {formError}
          </div>
        )}
        <form onSubmit={onSave} data-testid="donations-config-form">
          <div className="sgk-switchrow">
            <label className="sgk-toggle">
              <input
                type="checkbox"
                checked={donationsEnabled}
                onChange={(e) => setDonationsEnabled(e.target.checked)}
                data-testid="donations-enabled-checkbox"
              />
              <span className="sgk-toggle__track" />
            </label>
            <span className="sgk-switchrow__text">
              Accept donations
              {!config.charges_enabled && (
                <small> — Stripe must be ready before gifts can settle</small>
              )}
            </span>
          </div>

          <div className="sgk-field">
            <label htmlFor="don-min">Minimum gift (USD)</label>
            <input
              id="don-min"
              type="number"
              step="0.01"
              min="0.01"
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
              className="sgk-input sgk-input--narrow"
              data-testid="donations-min-amount-input"
            />
          </div>

          <p className="sgk-panel__hint">
            Gift amounts are fixed to the federal Section&nbsp;25F credit caps
            ($1,700 individual, $3,400 couple) and aren't configured here.
          </p>

          <div className="sgk-field">
            <label htmlFor="don-descriptor">
              Statement descriptor <span className="opt">(shown on the donor's card statement)</span>
            </label>
            <input
              id="don-descriptor"
              type="text"
              value={descriptor}
              onChange={(e) => setDescriptor(e.target.value)}
              className="sgk-input"
              data-testid="donations-descriptor-input"
            />
          </div>

          <div className="sgk-btnrow">
            <button
              type="submit"
              disabled={saveMutation.isPending}
              className="sgk-btn sgk-btn--gold sgk-btn--sm"
              data-testid="donations-save-button"
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
            {saved && <span className="sgk-saved" data-testid="donations-saved-message">Saved</span>}
          </div>
        </form>
      </section>
    </AdminLayout>
  );
};

export default DonationsSettings;
